import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { types } from "../api/index.js";
import { relativeTime, formatSize, shortDigest, apiLink } from "../utils/format.js";
import { uniqueStreams } from "../utils/canonical.js";
import "../components/copy-button.js";

type TriState = "" | "yes" | "no";

interface TagGroup {
  names: string[];
  canonical: string;
  variant: string;
  stream: string;
  digest: string;
  architectures: string[];
  sizes: Record<string, number>;
  oldest_created: string | null;
  deprecated: boolean;
  superseded: boolean;
}

@customElement("tags-tab")
export class TagsTab extends LitElement {
  @property() imageName = "";
  @property({ type: Array }) tags: types.Tag[] = [];
  @property({ type: Array }) variants: types.VariantDescription[] = [];
  @property() selectedCanonical = "";
  @state() private _baseFilter = "";
  @state() private _builderFilter: TriState = "";
  @state() private _fipsFilter: TriState = "";
  @state() private _streamFilter = "";
  @state() private _showSuperseded = false;

  createRenderRoot() {
    return this;
  }

  private get _variantMap(): Map<string, types.VariantDescription> {
    return new Map(this.variants.map((v) => [v.name, v]));
  }

  private _vd(tag: types.Tag): types.VariantDescription | undefined {
    return this._variantMap.get(tag.variant);
  }

  private get _bases(): string[] {
    return [...new Set(this.variants.map((v) => v.base))];
  }

  private get _baseDescriptions(): Map<string, string> {
    const map = new Map<string, string>();
    for (const v of this.variants) {
      if (v.description && !map.has(v.base)) map.set(v.base, v.description);
    }
    return map;
  }

  private get _hasBuilder(): boolean {
    return this.variants.some((v) => v.builder);
  }

  private get _hasFips(): boolean {
    return this.variants.some((v) => v.fips);
  }

  private get _streams(): string[] {
    return uniqueStreams(this.tags);
  }

  private _matchesVariantFilter(tag: types.Tag): boolean {
    const vd = this._vd(tag);
    if (this._baseFilter && vd?.base !== this._baseFilter) return false;
    if (this._builderFilter === "yes" && !vd?.builder) return false;
    if (this._builderFilter === "no" && vd?.builder) return false;
    if (this._fipsFilter === "yes" && !vd?.fips) return false;
    if (this._fipsFilter === "no" && vd?.fips) return false;
    return true;
  }

  private get _hasSuperseded(): boolean {
    let filtered = this.tags;
    if (this._streamFilter) filtered = filtered.filter((t) => t.stream === this._streamFilter);
    filtered = filtered.filter((t) => this._matchesVariantFilter(t));
    return filtered.some((t) => t.superseded);
  }

  private get _filteredGroups(): TagGroup[] {
    let filtered = this.tags;
    if (this._streamFilter) filtered = filtered.filter((t) => t.stream === this._streamFilter);
    filtered = filtered.filter((t) => this._matchesVariantFilter(t));
    if (!this._showSuperseded) filtered = filtered.filter((t) => !t.superseded);
    const byDigest = new Map<string, TagGroup>();
    for (const tag of filtered) {
      const existing = byDigest.get(tag.digest);
      if (existing) {
        existing.names.push(tag.name);
        if (tag.canonical.length > existing.canonical.length) existing.canonical = tag.canonical;
      } else {
        byDigest.set(tag.digest, {
          names: [tag.name],
          canonical: tag.canonical,
          variant: tag.variant,
          stream: tag.stream,
          digest: tag.digest,
          architectures: tag.architectures,
          sizes: tag.sizes,
          oldest_created: tag.oldest_created,
          deprecated: tag.deprecated === true,
          superseded: tag.superseded,
        });
      }
      if (existing) existing.deprecated ||= tag.deprecated === true;
    }
    return [...byDigest.values()].sort((a, b) => Number(a.superseded) - Number(b.superseded));
  }

  private _selectCanonical(canonical: string) {
    this.dispatchEvent(new CustomEvent("canonical-change", { detail: canonical }));
    this.dispatchEvent(new CustomEvent("tab-change", { detail: "details" }));
  }

  private _filterButton(label: string, active: boolean, handler: () => void, title = "") {
    return html`
      <button
        @click=${handler}
        title=${title}
        class="px-2 py-1 text-xs cursor-pointer transition-colors border -ml-px first:ml-0
               rounded-none first:rounded-l last:rounded-r
               ${active ? "bg-hb-accent text-hb-bg border-hb-accent relative z-10" : "bg-hb-surface text-gray-400 hover:text-gray-200 border-hb-border"}"
      >
        ${label}
      </button>
    `;
  }

  private _triStateGroup(label: string, value: TriState, setter: (v: TriState) => void) {
    return html`
      <div class="flex flex-wrap">
        ${this._filterButton(label, value === "", () => setter(""))}
        ${this._filterButton("yes", value === "yes", () => setter("yes"))}
        ${this._filterButton("no", value === "no", () => setter("no"))}
      </div>
    `;
  }

  render() {
    const groups = this._filteredGroups;
    const showStreamFilter = this._streams.length > 1;
    const bases = this._bases;
    const baseDescs = this._baseDescriptions;
    const showBaseFilter = bases.length > 1;
    const hasBuilder = this._hasBuilder;
    const hasFips = this._hasFips;

    return html`
      <div class="mb-4 flex flex-wrap items-center gap-3">
        ${showStreamFilter
          ? html`
              <div class="flex flex-wrap">
                ${this._filterButton("All streams", !this._streamFilter, () => (this._streamFilter = ""))}
                ${this._streams.map((s) => this._filterButton(s, this._streamFilter === s, () => (this._streamFilter = s)))}
              </div>
              <span class="text-gray-600">|</span>
            `
          : ""}
        ${showBaseFilter
          ? html`
              <div class="flex flex-wrap">
                ${this._filterButton("All", !this._baseFilter, () => (this._baseFilter = ""))}
                ${bases.map((b) => this._filterButton(b, this._baseFilter === b, () => (this._baseFilter = b), baseDescs.get(b) ?? ""))}
              </div>
            `
          : ""}
        ${hasBuilder
          ? this._triStateGroup("builder", this._builderFilter, (v) => (this._builderFilter = v))
          : ""}
        ${hasFips
          ? this._triStateGroup("fips", this._fipsFilter, (v) => (this._fipsFilter = v))
          : ""}
        ${this._hasSuperseded
          ? html`
              <span class="text-gray-600">|</span>
              <button
                @click=${() => (this._showSuperseded = !this._showSuperseded)}
                class="px-2 py-1 text-xs cursor-pointer transition-colors border rounded
                       ${this._showSuperseded
                         ? "bg-yellow-500/20 text-yellow-500 border-yellow-500/30"
                         : "bg-hb-surface text-gray-400 hover:text-gray-200 border-hb-border"}"
              >
                Superseded
              </button>
            `
          : ""}
        <div class="ml-auto">
          ${this.imageName ? apiLink(`/v1/images/${this.imageName}/tags`) : ""}
        </div>
      </div>

      <div class="border border-hb-border rounded-lg overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-hb-surface text-gray-400">
            <tr>
              <th class="py-2 px-3">Tags</th>
              ${showStreamFilter ? html`<th class="py-2 px-3 hidden sm:table-cell">Stream</th>` : ""}
              <th class="py-2 px-3">Variant</th>
              <th class="py-2 px-3">Digest</th>
              <th class="py-2 px-3 hidden sm:table-cell">Archs</th>
              <th class="py-2 px-3 hidden md:table-cell">Size</th>
              <th class="py-2 px-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            ${groups.map(
              (g) => html`
                <tr
                  @click=${() => this._selectCanonical(g.canonical)}
                  class="border-b border-hb-border hover:bg-hb-surface/50 cursor-pointer transition-colors
                         ${this.selectedCanonical === g.canonical ? "bg-hb-surface/30" : ""}
                         ${g.superseded ? "opacity-50" : ""}"
                >
                  <td class="py-2 px-3">
                    <div class="flex flex-wrap gap-1 items-center">
                      ${g.names.map(
                        (n) =>
                          html`<span class="px-1.5 py-0.5 rounded bg-hb-bg text-white text-xs border border-hb-border font-medium whitespace-nowrap"
                            >${n}</span
                          >`,
                      )}
                      ${g.superseded
                        ? html`<span class="px-1.5 py-0.5 rounded text-xs text-yellow-500/80 border border-yellow-500/30 whitespace-nowrap"
                            >superseded</span
                          >`
                        : ""}
                      ${g.deprecated
                        ? html`<span
                            class="px-1.5 py-0.5 rounded text-xs text-yellow-400 border border-yellow-500/30 bg-yellow-500/20 whitespace-nowrap"
                            title="Deprecated release"
                          >deprecated</span>`
                        : ""}
                    </div>
                  </td>
                  ${showStreamFilter
                    ? html`<td class="py-2 px-3 text-gray-400 hidden sm:table-cell">${g.stream}</td>`
                    : ""}
                  <td class="py-2 px-3">
                    <span class="px-1.5 py-0.5 rounded bg-hb-bg text-hb-accent/70 border border-hb-accent/30 whitespace-nowrap">
                      ${g.variant}
                    </span>
                  </td>
                  <td class="py-2 px-3 text-gray-400 font-mono">
                    <span class="inline-flex items-center gap-1">
                      <span title=${g.digest}>${shortDigest(g.digest)}</span>
                      <copy-button .value=${g.digest} label="digest"></copy-button>
                    </span>
                  </td>
                  <td class="py-2 px-3 text-gray-500 hidden sm:table-cell">${g.architectures.join(", ")}</td>
                  <td class="py-2 px-3 text-gray-500 hidden md:table-cell">${formatSize(Object.values(g.sizes)[0] ?? null)}</td>
                  <td class="py-2 px-3 text-gray-500" title=${g.oldest_created ?? ""}>${relativeTime(g.oldest_created)}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    `;
  }
}
